import { useState, useImperativeHandle, forwardRef, useCallback, useMemo } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import Cookies from 'universal-cookie';
import moment from 'moment';
import 'moment-timezone';

import { CloseIcon, PlusIcon } from 'Asset/icon';
import { checkIsSessionActive } from 'Helper/checkIsSessionActive';
import useGetAllAccountLivestream from 'Domain/schedule/useGetAllAccountLivestream';
import { TextRegular, TextSemiBold } from 'UiKit/typography';
import ButtonOutline from 'UiKit/button/buttonOutline';
import ButtonLink from 'UiKit/button/buttonLink';
import InputSelect from 'UiKit/input-select';
import InputComponent from 'UiKit/input';
import InputDatepicker from 'UiKit/input-datepicker';

import styles from './styles.module.css';

const SessionFields = forwardRef(({ t, giveawayIndex, watchedGiveaways, isEditing, onAllocationValidationChange, watchedBrand }, ref) => {
  const cookies = new Cookies();
  const timezone = cookies.get('organization')?.timezone;

  const {
    control,
    register,
    setValue,
    formState: { errors },
  } = useFormContext();
  const [stockErrors, setStockErrors] = useState({});

  const { data: accountOptions } = useGetAllAccountLivestream({ brand: watchedBrand });
  const {
    fields: sessions,
    append,
    remove,
  } = useFieldArray({
    control,
    name: `giveaways.${giveawayIndex}.sessions`,
  });

  const hourOptions = useMemo(() => {
    const options = [];
    for (let h = 0; h < 24; h += 1) {
      const hour = String(h).padStart(2, '0');
      const timeValue = timezone ? moment.tz(`${hour}:00`, 'HH:mm', timezone) : moment(`${hour}:00`, 'HH:mm');
      options.push({
        value: timeValue,
        label: `${hour}:00`,
      });
    }
    return options;
  }, [timezone]);

  const getHourFromValue = (value) => {
    if (!value) return null;
    return moment(value).hour();
  };

  const applyHourConstraints = (minHour, maxHour) => {
    return hourOptions.map((opt) => {
      const h = opt.value.hour();
      const isDisabled = (minHour != null && h < minHour) || (maxHour != null && h > maxHour);
      return { ...opt, isDisabled };
    });
  };

  const findOptionByValue = (value) => {
    if (!value) return null;
    const hour = getHourFromValue(value);
    if (hour == null) return null;
    return hourOptions.find((opt) => opt.value.hour() === hour) || null;
  };

  const handleAllocatedStockBlur = (sessionIndex) => {
    const totalStock = parseInt(watchedGiveaways?.[giveawayIndex]?.totalStock) || 0;
    const sessions = watchedGiveaways?.[giveawayIndex]?.sessions || [];
    const totalAllocated = sessions.reduce((sum, s) => sum + (parseInt(s?.allocatedStock) || 0), 0);

    const isInvalid = totalStock && totalAllocated > totalStock;
    onAllocationValidationChange?.(isInvalid);

    setStockErrors((prev) => ({
      ...prev,
      [sessionIndex]: isInvalid ? { message: `Total alokasi (${totalAllocated}) melebihi stok tersedia (${totalStock})` } : null,
    }));
  };

  const validateAllSessions = useCallback(() => {
    const totalStock = parseInt(watchedGiveaways?.[giveawayIndex]?.totalStock) || 0;
    const sessions = watchedGiveaways?.[giveawayIndex]?.sessions || [];
    const totalAllocated = sessions.reduce((sum, s) => sum + (parseInt(s?.allocatedStock) || 0), 0);

    const isInvalid = totalStock && totalAllocated > totalStock;
    onAllocationValidationChange?.(isInvalid);

    const newErrors = {};
    sessions.forEach((_, idx) => {
      newErrors[idx] = isInvalid ? { message: `Total alokasi (${totalAllocated}) melebihi stok tersedia (${totalStock})` } : null;
    });
    setStockErrors(newErrors);
  }, [watchedGiveaways, giveawayIndex, onAllocationValidationChange]);

  useImperativeHandle(
    ref,
    () => ({
      validateAllSessions,
    }),
    [validateAllSessions],
  );

  return (
    <Box className={styles['duration__fields-container']}>
      {sessions.map((session, sessionIndex) => {
        const sessionPath = `giveaways.${giveawayIndex}.sessions.${sessionIndex}`;
        const watchedSession = watchedGiveaways?.[giveawayIndex]?.sessions?.[sessionIndex] || {};
        const sessionErrors = errors?.giveaways?.[giveawayIndex]?.sessions?.[sessionIndex] || {};

        const endHour = getHourFromValue(watchedSession?.endTime);
        const startHour = getHourFromValue(watchedSession?.startTime);
        const startTimeOptions = applyHourConstraints(0, endHour != null ? endHour : 23);
        const endTimeOptions = applyHourConstraints(startHour != null ? startHour : 0, 23);

        return (
          <Flex key={session.id} className={styles['duration-session__item']} gap="12px">
            <Box flex={1}>
              <Flex gap="20px" alignItems="flex-start">
                <Box flex={3}>
                  <Controller
                    name={`${sessionPath}.accountLivestreamId`}
                    control={control}
                    render={({ field }) => (
                      <InputSelect
                        label={t('formGiveaway.platform')}
                        placeholder={t('formGiveaway.platform')}
                        value={field.value}
                        onChange={field.onChange}
                        isDisabled={!watchedBrand}
                        isRequired
                        options={accountOptions || []}
                        error={sessionErrors?.accountLivestreamId}
                      />
                    )}
                  />
                </Box>
                <Box flex={1}>
                  {(() => {
                    const rhfField = register(`${sessionPath}.allocatedStock`, {
                      required: 'Alokasi Stok wajib diisi',
                      min: { value: 1, message: 'Total Stok harus angka positif' },
                      onChange: (event) => {
                        let cleanedValue = event.target.value.replace(/[^0-9]/g, '').replace('-', '');
                        if (cleanedValue[0] === '0') {
                          cleanedValue = cleanedValue.slice(1);
                        }
                        setValue(`${sessionPath}.allocatedStock`, cleanedValue);
                      },
                    });
                    return (
                      <InputComponent
                        label={t('formGiveaway.allocation')}
                        placeholder="0"
                        isRequired
                        value={watchedSession?.allocatedStock ?? ''}
                        error={stockErrors[sessionIndex] || sessionErrors?.allocatedStock}
                        register={{
                          ...rhfField,
                          onBlur: (e) => {
                            rhfField.onBlur(e);
                            handleAllocatedStockBlur(sessionIndex);
                          },
                        }}
                      />
                    );
                  })()}
                </Box>
              </Flex>
              <Flex alignItems="flex-end" marginTop="20px">
                <Controller
                  name={`${sessionPath}.promotionDate`}
                  control={control}
                  rules={{ required: 'Tanggal wajib diisi' }}
                  render={({ field }) => (
                    <InputDatepicker
                      label={t('formGiveaway.date')}
                      placeholder="DD/MM/YYYY"
                      margin="0 24px 0 0"
                      isRequired
                      value={field.value}
                      onChange={field.onChange}
                      error={sessionErrors?.promotionDate}
                    />
                  )}
                />
                <Box flex={1}>
                  <TextRegular className={styles['duration-session__label']}>{t('formGiveaway.time')}</TextRegular>
                  <Flex alignItems="center">
                    <Box flex={1}>
                      <Controller
                        name={`${sessionPath}.startTime`}
                        control={control}
                        rules={{ required: 'Waktu mulai wajib diisi' }}
                        render={({ field }) => (
                          <InputSelect
                            placeholder="HH:00"
                            isSearchable={false}
                            options={startTimeOptions}
                            value={findOptionByValue(field.value)}
                            onChange={(option) => field.onChange(option?.value ?? null)}
                            error={sessionErrors?.startTime}
                          />
                        )}
                      />
                    </Box>
                    <TextSemiBold className={styles['duration-session__divider']}>-</TextSemiBold>
                    <Box flex={1}>
                      <Controller
                        name={`${sessionPath}.endTime`}
                        control={control}
                        rules={{ required: 'Waktu selesai wajib diisi' }}
                        render={({ field }) => (
                          <InputSelect
                            placeholder="HH:00"
                            isSearchable={false}
                            options={endTimeOptions}
                            value={findOptionByValue(field.value)}
                            onChange={(option) => field.onChange(option?.value ?? null)}
                            error={sessionErrors?.endTime}
                          />
                        )}
                      />
                    </Box>
                  </Flex>
                </Box>
              </Flex>
            </Box>
            {sessions.length > 1 && (!isEditing || !checkIsSessionActive(watchedSession)) && (
              <ButtonOutline borderWidth="1.5px" color="var(--color-error50)" padding="6px" width="28px" height="28px" onClick={() => remove(sessionIndex)}>
                <CloseIcon color="var(--color-error50)" size="11px" />
              </ButtonOutline>
            )}
          </Flex>
        );
      })}
      <Box>
        <ButtonLink
          leftIcon={<PlusIcon width="14px" height="14px" color="var(--color-primary70)" />}
          onClick={() => append({ promotionDate: '', startTime: null, endTime: null, allocatedStock: null, accountLivestreamId: null })}
        >
          <TextSemiBold className={styles['duration-session__text--button-add']}>{t('formGiveaway.addDuration')}</TextSemiBold>
        </ButtonLink>
      </Box>
    </Box>
  );
});

export default SessionFields;
